import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance renewal handling — separate setting, same values/labels as
// Maintenance Handling. Automated only when BOTH are Estatel AI manages.
export default function ComplianceHandlingSettings() {
  const [s, setS] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/compliance/renewal-settings`, { credentials: 'include' });
    if (res.ok) setS(await res.json());
    else setErr('Could not load compliance handling.');
  }, []);

  React.useEffect(() => {
    load();
    window.addEventListener('maintenance-settings-saved', load);
    return () => window.removeEventListener('maintenance-settings-saved', load);
  }, [load]);

  const choose = async (mode) => {
    setSaving(true); setErr(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compliance/renewal-settings`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ compliance_autonomy: mode }),
      });
      if (res.ok) setS(await res.json()); else setErr('Could not save.');
    } finally { setSaving(false); }
  };

  if (!s) return <div data-testid="compliance-handling-loading">{err || 'Loading…'}</div>;
  const locked = s.set_by_maintenance;
  const current = locked ? 'managed_by_agent' : s.compliance_autonomy;

  const OPTION = (id, title, body) => (
    <label key={id} data-testid={`compliance-mode-${id}`}
      style={{ display: 'block', border: current === id ? '2px solid #ea580c' : '1px solid #e2e8f0',
               background: current === id ? '#fff7ed' : '#fff', borderRadius: 12,
               padding: '1rem 1.1rem', marginBottom: 12, cursor: locked ? 'not-allowed' : 'pointer',
               opacity: locked ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <input type="radio" name="compliance-mode" value={id} checked={current === id}
          disabled={locked || saving} onChange={() => choose(id)} style={{ marginTop: 4 }} />
        <div>
          <strong style={{ color: '#0f172a' }}>{title}</strong>
          <p style={{ margin: '4px 0 0', color: '#475569', fontSize: '0.9rem' }}>{body}</p>
        </div>
      </div>
    </label>
  );

  return (
    <div data-testid="compliance-handling" style={{ marginTop: 32 }}>
      <h2 style={{ marginTop: 0 }}>Compliance renewals</h2>
      <p style={{ color: '#64748b', maxWidth: 720 }} data-testid="compliance-mode-scope-note">
        Applies to renewing Gas Safety, EICR and EPC certificates only — contacting contractors and
        tenants to get them renewed. Expiry tracking and your 20, 10 and 5-day and expiry-day alerts
        carry on either way.
      </p>
      {locked && (
        <p data-testid="compliance-mode-set-by-maintenance"
          style={{ background: '#f1f5f9', borderRadius: 8, padding: '0.6rem 0.8rem', color: '#334155', maxWidth: 720 }}>
          Set by Maintenance Handling. Renewals run on the maintenance engine, so they're automated
          only when Maintenance Handling is Estatel AI manages.
        </p>
      )}
      {OPTION('managed_by_estateiq', 'Estatel AI manages',
        'From 20 days before expiry, Estatel AI asks a qualified contractor for price and availability, brings the quote to you for approval, then arranges access with the tenant and books the visit.')}
      {OPTION('managed_by_agent', 'Agent manages',
        'Estatel AI tracks expiry dates and alerts you, but contacts no contractor or tenant. Arrange renewals yourself.')}
      {err && <p style={{ color: '#b91c1c' }} data-testid="compliance-mode-error">{err}</p>}
    </div>
  );
}
